import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ScriptableContext,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface StockDeltaData {
  week_offset: number
  net_delta: number
  signal: string
  expiry_date?: string
}

interface StockDeltaChartProps {
  symbol: string
  data: StockDeltaData[]
  height?: number
}

const StockDeltaChart: React.FC<StockDeltaChartProps> = ({ symbol, data, height = 200 }) => {
  // Sort data by week offset
  const sortedData = [...data].sort((a, b) => a.week_offset - b.week_offset)
  
  // Calculate max absolute value for symmetric Y-axis
  const maxAbsValue = Math.max(...sortedData.map(d => Math.abs(d.net_delta)))
  const yAxisMax = maxAbsValue * 1.2 // Add 20% padding
  const yAxisMin = -yAxisMax // Make it symmetric
  
  // Format dates for labels
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return ''
    // Parse as local date to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day) // month is 0-indexed
    
    // Always show year for clarity since these are future option expiries
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
  }
  
  const chartData = {
    labels: sortedData.map(d => d.expiry_date ? formatDate(d.expiry_date) : `Week ${d.week_offset}`),
    datasets: [
      {
        label: `${symbol} Signal`,
        data: sortedData.map(d => d.net_delta),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const chart = context.chart
          const ctx = chart.ctx
          const chartArea = chart.chartArea
          
          if (!chartArea) {
            return 'transparent'
          }
          
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
          
          // Calculate where zero is on the chart
          const yScale = chart.scales?.y
          if (yScale) {
            const zeroY = yScale.getPixelForValue(0)
            const topY = chartArea.top
            const bottomY = chartArea.bottom
            
            // Calculate the position of zero as a percentage
            const zeroPosition = (zeroY - topY) / (bottomY - topY)
            
            // Green above zero, red below zero
            gradient.addColorStop(0, 'rgba(34, 197, 94, 0.2)') // Green at top
            gradient.addColorStop(Math.max(0, Math.min(1, zeroPosition - 0.01)), 'rgba(34, 197, 94, 0.1)')
            gradient.addColorStop(Math.max(0, Math.min(1, zeroPosition)), 'rgba(255, 255, 255, 0)') // Transparent at zero
            gradient.addColorStop(Math.max(0, Math.min(1, zeroPosition + 0.01)), 'rgba(239, 68, 68, 0.1)')
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0.2)') // Red at bottom
          } else {
            // Fallback gradient if scales not available
            gradient.addColorStop(0, 'rgba(34, 197, 94, 0.1)')
            gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0)')
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0.1)')
          }
          
          return gradient
        },
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: sortedData.map(d => 
          d.signal === 'Bullish' ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
        ),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  }

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${symbol} - Next 6 Weeks Options Flow`,
        font: {
          size: 14,
          weight: 'bold',
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y
            const formatted = new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(Math.abs(value))
            
            const signal = sortedData[context.dataIndex].signal
            return `Signal: ${value >= 0 ? '+' : '-'}${formatted} (${signal})`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 10,
          },
        },
      },
      y: {
        min: yAxisMin,
        max: yAxisMax,
        grid: {
          color: (context) => {
            // Make zero line heavier and darker
            if (context.tick.value === 0) {
              return 'rgba(0, 0, 0, 0.3)'
            }
            return 'rgba(0, 0, 0, 0.05)'
          },
          lineWidth: (context) => {
            // Make zero line thicker
            if (context.tick.value === 0) {
              return 2
            }
            return 1
          },
        },
        ticks: {
          callback: function(tickValue: string | number) {
            const value = Number(tickValue)
            if (Math.abs(value) >= 1e6) {
              return (value / 1e6).toFixed(1) + 'M'
            } else if (Math.abs(value) >= 1e3) {
              return (value / 1e3).toFixed(0) + 'K'
            }
            return value.toFixed(0)
          },
          font: {
            size: 10,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  }

  return (
    <div style={{ height, overflow: 'hidden', position: 'relative' }}>
      <Line data={chartData} options={options} />
    </div>
  )
}

export default StockDeltaChart